import { useMemo } from "react";
import { useStore } from "ra-core";

import { resolveEffectiveDealStages } from "../deals/stageConfig";
import type { DealStage, LabeledValue, NoteStatus } from "../types";
import { defaultConfiguration } from "./defaultConfiguration";

export const CONFIGURATION_STORE_KEY = "app.configuration";

/**
 * Almikia Epic EP (TASK-106): the E2 fallback of `dealStages`, but resolved
 * through `resolveEffectiveDealStages([])` rather than the plain
 * `defaultDealStages` list -- so `placement`/`archived` are always present,
 * even before `useConfigurationLoader`'s `deal_stage_configs` query settles
 * (or when it is never seeded). Every consumer that derives kanban columns
 * or special-state tabs from `placement` (TASK-106) needs this on the very
 * first render, not only once the query resolves. Computed once at module
 * load (pure, same result every call) rather than per-render.
 */
const DEFAULT_EFFECTIVE_DEAL_STAGES = resolveEffectiveDealStages([]);

/**
 * Almikia Epic EP (TASK-104): the resolved, effective `dealStages` list
 * (DATA CONTRACT EP (8): configured labels/order/placement/archived, E2
 * defaults for a missing system row) -- kept in its OWN store key, separate
 * from `CONFIGURATION_STORE_KEY`, so a `configuration` row's legacy
 * `dealStages` (still saved by `SettingsPage.tsx`) is never read back. The
 * default value (`DEFAULT_EFFECTIVE_DEAL_STAGES` above) carries the exact
 * same values/labels/order as today's `defaultDealStages` (see
 * `defaultConfiguration.ts`), plus `placement`/`archived`, so this store's
 * fallback IS the E2 fallback that applies while `useConfigurationLoader`'s
 * query is loading or failed.
 */
export const DEAL_STAGES_STORE_KEY = "app.dealStages";

export interface ConfigurationContextValue {
  companySectors: LabeledValue[];
  currency: string;
  dealCategories: LabeledValue[];
  dealPipelineStatuses: string[];
  dealStages: DealStage[];
  noteStatuses: NoteStatus[];
  taskTypes: LabeledValue[];
  title: string;
  darkModeLogo: string;
  lightModeLogo: string;
}

export const useConfigurationContext = () => {
  const [config] = useStore<ConfigurationContextValue>(
    CONFIGURATION_STORE_KEY,
    defaultConfiguration,
  );
  // Almikia Epic EP (TASK-104): the effective `dealStages`
  // (`deal_stage_configs` resolved by `useConfigurationLoader`), defaulting
  // to the E2 list while it is loading, failed to load, or was never seeded.
  const [dealStages] = useStore<ConfigurationContextValue["dealStages"]>(
    DEAL_STAGES_STORE_KEY,
    DEFAULT_EFFECTIVE_DEAL_STAGES,
  );
  // Merge with defaults so that missing fields in stored config
  // fall back to default values (e.g. when new settings are added).
  // dealStages/dealPipelineStatuses always win over the stored `configuration`
  // row: a row saved before Almikia Epic E2 may still hold the legacy Atomic
  // stages, and this app must never read them back (E2, TASK-009). dealStages
  // itself comes from the dedicated store above, never from `config`.
  return useMemo(
    () => ({
      ...defaultConfiguration,
      ...config,
      dealStages,
      dealPipelineStatuses: defaultConfiguration.dealPipelineStatuses,
    }),
    [config, dealStages],
  );
};

export const useConfigurationUpdater = () => {
  const [, setConfig] = useStore<ConfigurationContextValue>(
    CONFIGURATION_STORE_KEY,
  );
  return setConfig;
};

/** Setter for the effective `dealStages` store — see {@link DEAL_STAGES_STORE_KEY}. */
export const useDealStagesUpdater = () => {
  const [, setDealStages] = useStore<ConfigurationContextValue["dealStages"]>(
    DEAL_STAGES_STORE_KEY,
  );
  return setDealStages;
};
